import { DropdownSelect } from '@components/common/DropdownSelect';
import { useAppSelector } from '@hooks/useRedux';
import {
  useCreateRatePlan,
  useRatePlans,
  useUpdateRatePlan,
} from '@hooks/accommodation';
import { selectAccommodationProperty } from '@store/accommodation';
import { Edit2, Plus, X } from 'lucide-react';
import React, { useState } from 'react';
import {
  AccommodationMealPlan,
  AccommodationRatePlan,
} from '../../../../types/accommodation';

const MEAL_PLAN_LABELS: Record<AccommodationMealPlan, string> = {
  room_only: 'Room Only',
  breakfast: 'Bed & Breakfast',
  half_board: 'Half Board',
  full_board: 'Full Board',
};

interface RatePlanFormState {
  name: string;
  description: string;
  meal_plan: AccommodationMealPlan | '';
  cancellation_policy: string;
}

const emptyForm = (): RatePlanFormState => ({
  name: '',
  description: '',
  meal_plan: '',
  cancellation_policy: '',
});

export const RatePlansSection: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const { data: allRatePlans = [] } = useRatePlans(property?.id);
  const ratePlans = allRatePlans.filter((rp) => rp.is_active);
  const createRatePlan = useCreateRatePlan();
  const updateRatePlan = useUpdateRatePlan();
  const isSubmitting = createRatePlan.isPending || updateRatePlan.isPending;

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AccommodationRatePlan | null>(null);
  const [form, setForm] = useState<RatePlanFormState>(emptyForm());

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (plan: AccommodationRatePlan) => {
    setEditing(plan);
    setForm({
      name: plan.name,
      description: plan.description ?? '',
      meal_plan: plan.meal_plan ?? '',
      cancellation_policy: plan.cancellation_policy ?? '',
    });
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!property) return;

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      meal_plan: (form.meal_plan as AccommodationMealPlan) || null,
      cancellation_policy: form.cancellation_policy.trim() || null,
    };

    if (editing) {
      updateRatePlan.mutate(
        { id: editing.id, updates: payload },
        { onSuccess: handleClose },
      );
    } else {
      createRatePlan.mutate(
        { ...payload, property_id: property.id },
        { onSuccess: handleClose },
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Rate plans</h3>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center gap-1.5 rounded-lg bg-pos-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-pos-primary/90"
        >
          <Plus size={13} />
          Add plan
        </button>
      </div>

      {ratePlans.length === 0 ? (
        <p className="text-sm text-gray-400">No rate plans yet.</p>
      ) : (
        <div className="space-y-2">
          {ratePlans.map((plan) => (
            <div
              key={plan.id}
              className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3"
            >
              <div>
                <p className="text-sm font-medium text-gray-900">{plan.name}</p>
                {plan.meal_plan && (
                  <p className="text-xs text-gray-500">
                    {MEAL_PLAN_LABELS[plan.meal_plan]}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => openEdit(plan)}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <Edit2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="font-semibold text-gray-900">
                {editing ? 'Edit rate plan' : 'New rate plan'}
              </h4>
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="rp-name"
                  className="mb-1 block text-xs font-medium text-gray-700"
                >
                  Name *
                </label>
                <input
                  id="rp-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                  placeholder="e.g. Non-Refundable"
                />
              </div>

              <DropdownSelect
                label="Meal plan"
                value={form.meal_plan}
                onChange={(v) =>
                  setForm({
                    ...form,
                    meal_plan: v as AccommodationMealPlan | '',
                  })
                }
                options={[
                  { value: '', label: '— none —' },
                  ...(
                    Object.entries(MEAL_PLAN_LABELS) as [
                      AccommodationMealPlan,
                      string,
                    ][]
                  ).map(([val, label]) => ({ value: val, label })),
                ]}
              />

              <div>
                <label
                  htmlFor="rp-cancel"
                  className="mb-1 block text-xs font-medium text-gray-700"
                >
                  Cancellation policy
                </label>
                <textarea
                  id="rp-cancel"
                  rows={3}
                  value={form.cancellation_policy}
                  onChange={(e) =>
                    setForm({ ...form, cancellation_policy: e.target.value })
                  }
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                  placeholder="e.g. Free cancellation up to 48 hours before check-in."
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
                >
                  {isSubmitting
                    ? 'Saving…'
                    : editing
                      ? 'Save changes'
                      : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
